import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const createCustomIcon = (color = "#007bff", size = 32) => {
  return L.divIcon({
    html: `
      <div style="position:relative;width:${size}px;height:${size}px;">
        <div style="position:absolute;width:${size}px;height:${size}px;background-color:${color};border-radius:50%;opacity:0.25;transform:translate(-50%,-50%);top:50%;left:50%;"></div>
        <div style="position:absolute;width:${size * 0.625}px;height:${size * 0.625}px;background-color:${color};border-radius:50%;border:2.5px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);transform:translate(-50%,-50%);top:50%;left:50%;"></div>
        <div style="position:absolute;width:${size * 0.25}px;height:${size * 0.25}px;background-color:white;border-radius:50%;transform:translate(-50%,-50%);top:50%;left:50%;"></div>
      </div>
    `,
    className: "custom-marker",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2 + 4)],
  });
};

const getDotColor = (scanne) =>
  scanne === true || scanne === 1 ? "#28a745" : "#e53935";

const convertCoordinate = (coord) => {
  const num = Number(coord);
  if (isNaN(num)) return NaN;
  return Math.abs(num) > 180 ? num / 10000 : num;
};

const ScanBadge = ({ scanne }) => {
  const ok = scanne === true || scanne === 1;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 9px",
        borderRadius: 20,
        fontSize: "0.76rem",
        fontWeight: 600,
        backgroundColor: ok ? "#e8f5e9" : "#ffebee",
        color: ok ? "#2e7d32" : "#c62828",
        border: `1px solid ${ok ? "#a5d6a7" : "#ef9a9a"}`,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: ok ? "#43a047" : "#e53935",
          display: "inline-block",
        }}
      />
      {ok ? "Scanné" : "Non scanné"}
    </span>
  );
};

const MapModal = ({
  isOpen,
  onClose,
  latitude,
  longitude,
  siteName = "Site",
  agentName = "",
  date = "",
  rondeStatus = "Terminée",
  initialZoom = 14,
  allRondes = null,
  showAllRondes = false,
  title = "",
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const pointsRef = useRef([]);
  const [zoomLevel, setZoomLevel] = useState(initialZoom);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const buildPoints = () => {
    if (showAllRondes && allRondes && allRondes.length > 0) {
      return allRondes
        .map((r) => ({
          lat: convertCoordinate(r.latitude),
          lng: convertCoordinate(r.longitude),
          nom: r.titre || r.site?.nom || "Site",
          scanne: r.scanne,
          statut: r.statut,
        }))
        .filter(
          (p) => !isNaN(p.lat) && !isNaN(p.lng) && p.lat !== 0 && p.lng !== 0,
        );
    }
    const lat = convertCoordinate(latitude);
    const lng = convertCoordinate(longitude);
    if (isNaN(lat) || isNaN(lng)) return [];
    return [
      {
        lat,
        lng,
        nom: siteName,
        scanne: rondeStatus === "Terminée" || rondeStatus === 2,
        statut: rondeStatus,
      },
    ];
  };

  const fitAll = (map, markers, points) => {
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 15);
    } else {
      map.fitBounds(L.featureGroup(markers).getBounds().pad(0.25));
    }
  };

  const buildTooltipHtml = (point) => {
    const ok = point.scanne === true || point.scanne === 1;
    const color = getDotColor(point.scanne);
    return `
      <div style="min-width:200px;font-family:inherit;background:#fff;border-radius:10px;padding:11px 13px;box-shadow:0 3px 14px rgba(0,0,0,0.18);border:1.5px solid ${color};">
        <div style="display:flex;align-items:center;gap:7px;margin-bottom:9px;padding-bottom:8px;border-bottom:1px solid #eee;">
          <div style="width:10px;height:10px;border-radius:50%;background:${color};flex-shrink:0;"></div>
          <strong style="color:#0B3C5D;font-size:0.88rem;line-height:1.3;">${point.nom}</strong>
        </div>
        <div style="display:flex;justify-content:space-between;margin-bottom:5px;font-size:0.78rem;">
          <span style="color:#999;font-weight:500;">Latitude</span>
          <span style="font-weight:700;color:#333;font-family:monospace;">${point.lat.toFixed(5)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;margin-bottom:8px;font-size:0.78rem;">
          <span style="color:#999;font-weight:500;">Longitude</span>
          <span style="font-weight:700;color:#333;font-family:monospace;">${point.lng.toFixed(5)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;font-size:0.78rem;">
          <span style="color:#999;font-weight:500;">Statut scan</span>
          <span style="padding:2px 10px;border-radius:20px;font-size:0.75rem;font-weight:700;background:${ok ? "#e8f5e9" : "#ffebee"};color:${ok ? "#2e7d32" : "#c62828"};border:1px solid ${ok ? "#a5d6a7" : "#ef9a9a"};">
            ${ok ? "✓ Scanné" : "✗ Non scanné"}
          </span>
        </div>
      </div>`;
  };

  // Init map
  useEffect(() => {
    if (!isOpen) return;
    if (!document.getElementById("leaflet-attribution-hide")) {
      const s = document.createElement("style");
      s.id = "leaflet-attribution-hide";
      s.textContent = ".leaflet-control-attribution{display:none!important;}";
      document.head.appendChild(s);
    }
    if (mapRef.current || !mapContainerRef.current) return;

    const points = buildPoints();
    pointsRef.current = points;
    if (points.length === 0) {
      setError("Aucune coordonnée GPS valide disponible.");
      return;
    }

    const map = L.map(mapContainerRef.current, {
      zoomControl: false,
      attributionControl: false,
      minZoom: 3,
      maxZoom: 19,
    }).setView([points[0].lat, points[0].lng], initialZoom);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: false,
    }).addTo(map);
    L.control.zoom({ position: "bottomright" }).addTo(map);

    const leafletMarkers = points.map((point, idx) => {
      const marker = L.marker([point.lat, point.lng], {
        icon: createCustomIcon(getDotColor(point.scanne)),
        title: point.nom,
      }).addTo(map);

      // Tooltip permanent — toujours visible sans clic
      marker.bindTooltip(buildTooltipHtml(point), {
        permanent: true,
        direction: "top",
        className: "leaflet-tooltip-clean",
        opacity: 1,
        offset: [0, -8],
      });

      marker.on("click", () => setActiveIndex(idx));
      return marker;
    });

    // CSS pour supprimer le style par défaut des tooltips Leaflet
    if (!document.getElementById("leaflet-tooltip-clean-style")) {
      const s = document.createElement("style");
      s.id = "leaflet-tooltip-clean-style";
      s.textContent = `
        .leaflet-tooltip-clean {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          padding: 0 !important;
          border-radius: 0 !important;
          white-space: normal !important;
        }
        .leaflet-tooltip-clean::before {
          display: none !important;
        }
      `;
      document.head.appendChild(s);
    }

    markersRef.current = leafletMarkers;
    mapRef.current = map;
    setError(null);

    setTimeout(() => {
      if (!mapRef.current) return;
      mapRef.current.invalidateSize();
      fitAll(map, leafletMarkers, points);
      setZoomLevel(Math.round(map.getZoom()));
    }, 300);

    map.on("zoomend", () => setZoomLevel(Math.round(map.getZoom())));
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && mapRef.current)
      setTimeout(() => mapRef.current.invalidateSize(true), 100);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markersRef.current = [];
      pointsRef.current = [];
      const s = document.getElementById("leaflet-attribution-hide");
      if (s) s.remove();
    };
  }, []);

  const handleCardClick = (idx) => {
    setActiveIndex(idx);
    const point = pointsRef.current[idx];
    if (!mapRef.current || !point) return;
    mapRef.current.setView([point.lat, point.lng], 16, { animate: true });
  };

  const handleResetView = () => {
    if (mapRef.current && markersRef.current.length > 0) {
      fitAll(mapRef.current, markersRef.current, pointsRef.current);
      setZoomLevel(Math.round(mapRef.current.getZoom()));
      setActiveIndex(null);
    }
  };

  if (!isOpen) return null;

  const points = buildPoints();
  const scannedCount = points.filter(
    (p) => p.scanne === true || p.scanne === 1,
  ).length;
  const totalCount = points.length;

  return (
    <div
      className="modal show"
      style={{
        display: "block",
        backgroundColor: "rgba(0,0,0,0.75)",
        zIndex: 1050,
      }}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: 1120, margin: "1.75rem auto" }}
      >
        <div
          className="modal-content"
          style={{
            borderRadius: 12,
            overflow: "hidden",
            border: "none",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}
        >
          {/* HEADER */}
          <div
            style={{
              background: "linear-gradient(135deg, #0B3C5D 0%, #1E88C8 100%)",
              padding: "14px 20px",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i
                className="bi bi-map text-white"
                style={{ fontSize: "1.15rem" }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <h5
                className="mb-0 text-white"
                style={{ fontWeight: 700, fontSize: "1rem" }}
              >
                {title ||
                  (showAllRondes
                    ? "Sites de la ronde"
                    : `Localisation — ${siteName}`)}
              </h5>
              <small
                style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.78rem" }}
              >
                {totalCount} site(s) &nbsp;·&nbsp;
                <span style={{ color: "#a5d6a7" }}>
                  {scannedCount} scanné(s)
                </span>
                &nbsp;·&nbsp;
                <span style={{ color: "#ffcc80" }}>
                  {totalCount - scannedCount} en attente
                </span>
              </small>
            </div>
            <button
              className="btn-close btn-close-white"
              onClick={onClose}
              style={{ opacity: 0.8 }}
            />
          </div>

          {/* BODY */}
          <div style={{ display: "flex", height: 560 }}>
            {error ? (
              <div className="d-flex align-items-center justify-content-center w-100 text-danger">
                <div className="text-center">
                  <i className="bi bi-exclamation-triangle fs-1" />
                  <p className="mt-2">{error}</p>
                </div>
              </div>
            ) : (
              <>
                {/* Carte */}
                <div style={{ flex: 1, position: "relative" }}>
                  <div
                    ref={mapContainerRef}
                    style={{ height: "100%", width: "100%" }}
                  />
                  <button
                    onClick={handleResetView}
                    title="Vue d'ensemble"
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      zIndex: 1000,
                      background: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 12px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: "#0B3C5D",
                    }}
                  >
                    <i className="bi bi-arrows-fullscreen" /> Vue d'ensemble
                  </button>
                  <div
                    style={{
                      position: "absolute",
                      bottom: 10,
                      left: 10,
                      zIndex: 1000,
                      background: "rgba(255,255,255,0.9)",
                      borderRadius: 6,
                      padding: "3px 10px",
                      fontSize: "0.73rem",
                      color: "#666",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                    }}
                  >
                    <i className="bi bi-search me-1" />
                    Zoom {zoomLevel}x
                  </div>
                </div>

                {/* Panneau latéral */}
                <div
                  style={{
                    width: 295,
                    flexShrink: 0,
                    borderLeft: "1px solid #e9ecef",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  {/* En-tête panneau */}
                  <div
                    style={{
                      padding: "11px 14px",
                      borderBottom: "1px solid #e9ecef",
                      backgroundColor: "#fff",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 6,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          color: "#0B3C5D",
                          fontSize: "0.88rem",
                        }}
                      >
                        <i className="bi bi-list-ul me-1" />
                        Sites ({totalCount})
                      </span>
                      <ScanBadge
                        scanne={scannedCount === totalCount && totalCount > 0}
                      />
                    </div>
                    {/* Progress bar */}
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#888",
                        marginBottom: 4,
                      }}
                    >
                      {scannedCount}/{totalCount} scannés
                    </div>
                    <div
                      style={{
                        height: 5,
                        borderRadius: 3,
                        backgroundColor: "#e0e0e0",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          borderRadius: 3,
                          width: `${totalCount ? (scannedCount / totalCount) * 100 : 0}%`,
                          background: "linear-gradient(90deg,#43a047,#66bb6a)",
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>

                  {/* Cards */}
                  <div
                    style={{ overflowY: "auto", flex: 1, padding: "8px 10px" }}
                  >
                    {points.map((point, idx) => {
                      const isActive = activeIndex === idx;
                      const dotColor = getDotColor(point.scanne);
                      return (
                        <div
                          key={idx}
                          onClick={() => handleCardClick(idx)}
                          style={{
                            backgroundColor: isActive ? "#e3f2fd" : "#fff",
                            border: `1.5px solid ${isActive ? "#1E88C8" : "#e9ecef"}`,
                            borderRadius: 10,
                            padding: "10px 11px",
                            marginBottom: 7,
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                            boxShadow: isActive
                              ? "0 2px 10px rgba(30,136,200,0.15)"
                              : "0 1px 3px rgba(0,0,0,0.05)",
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive)
                              e.currentTarget.style.borderColor = "#90caf9";
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive)
                              e.currentTarget.style.borderColor = "#e9ecef";
                          }}
                        >
                          {/* Nom + badge */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 6,
                              marginBottom: 8,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 7,
                                minWidth: 0,
                              }}
                            >
                              <div
                                style={{
                                  width: 9,
                                  height: 9,
                                  borderRadius: "50%",
                                  backgroundColor: dotColor,
                                  flexShrink: 0,
                                  marginTop: 3,
                                }}
                              />
                              <span
                                style={{
                                  fontWeight: 700,
                                  color: "#0B3C5D",
                                  fontSize: "0.85rem",
                                  lineHeight: 1.35,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {point.nom}
                              </span>
                            </div>
                            <ScanBadge scanne={point.scanne} />
                          </div>

                          {/* Coordonnées */}
                          <div
                            style={{
                              backgroundColor: isActive
                                ? "rgba(255,255,255,0.75)"
                                : "#f5f7fa",
                              borderRadius: 7,
                              padding: "7px 10px",
                              fontSize: "0.77rem",
                              color: "#555",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: 4,
                              }}
                            >
                              <span style={{ color: "#999", fontWeight: 500 }}>
                                Latitude
                              </span>
                              <span
                                style={{
                                  fontWeight: 700,
                                  color: "#333",
                                  fontFamily: "monospace",
                                }}
                              >
                                {point.lat.toFixed(5)}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span style={{ color: "#999", fontWeight: 500 }}>
                                Longitude
                              </span>
                              <span
                                style={{
                                  fontWeight: 700,
                                  color: "#333",
                                  fontFamily: "monospace",
                                }}
                              >
                                {point.lng.toFixed(5)}
                              </span>
                            </div>
                          </div>

                          {isActive && (
                            <div
                              style={{
                                marginTop: 6,
                                fontSize: "0.73rem",
                                color: "#1E88C8",
                                fontWeight: 600,
                                textAlign: "center",
                              }}
                            >
                              <i className="bi bi-geo-alt-fill me-1" />
                              Centré sur la carte
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Légende */}
                  <div
                    style={{
                      padding: "9px 14px",
                      borderTop: "1px solid #e9ecef",
                      backgroundColor: "#fff",
                      fontSize: "0.74rem",
                      color: "#999",
                      display: "flex",
                      gap: 16,
                    }}
                  >
                    <span>
                      <span style={{ color: "#43a047", fontWeight: 800 }}>
                        ●
                      </span>{" "}
                      Scanné
                    </span>
                    <span>
                      <span style={{ color: "#e53935", fontWeight: 800 }}>
                        ●
                      </span>{" "}
                      Non scanné
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* FOOTER */}
          <div
            style={{
              padding: "9px 20px",
              backgroundColor: "#f8f9fa",
              borderTop: "1px solid #e9ecef",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <small style={{ color: "#aaa", fontSize: "0.76rem" }}>
              <i className="bi bi-hand-index me-1" />
              Cliquez sur une card ou un marqueur pour les détails
            </small>
            <button
              className="btn btn-secondary btn-sm"
              onClick={onClose}
              style={{ borderRadius: 8, padding: "5px 18px" }}
            >
              <i className="bi bi-x-circle me-1" />
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapModal;
